/**
 * MembersPage - manage organization members and invitations
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@stores/authStore';
import { organizationsApi, type OrganizationMember } from '@services/api';
import { useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@components/ui/Toast';

export default function MembersPage() {
  const { t } = useTranslation();
  const { currentOrganization, user } = useAuthStore();

  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  useEffect(() => {
    if (currentOrganization) {
      loadMembers();
    }
  }, [currentOrganization]);

  const loadMembers = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    setError(null);

    try {
      const membersList = await organizationsApi.getMembers(currentOrganization.id);
      setMembers(membersList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization) return;

    setIsInviting(true);
    setInviteError(null);
    setInviteSuccess(false);

    try {
      await organizationsApi.inviteMember(currentOrganization.id, {
        email: inviteEmail,
        role: inviteRole,
      });
      setInviteSuccess(true);
      setInviteEmail('');
      setInviteRole('member');
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteSuccess(false);
      }, 2000);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: 'member' | 'admin') => {
    if (!currentOrganization) return;

    try {
      await organizationsApi.updateMemberRole(currentOrganization.id, memberId, newRole);
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m))
      );
    } catch (err) {
      console.error('Failed to update role:', err);
      toast.error(err instanceof Error ? err.message : t('settings.failedToUpdateRole', 'Failed to update role'));
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!currentOrganization) return;

    const confirmed = await confirm({
      title: t('settings.removeMember'),
      description: t('settings.confirmRemoveMember') + ` (${memberName})`,
      confirmLabel: t('common.remove'),
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await organizationsApi.removeMember(currentOrganization.id, memberId);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch (err) {
      console.error('Failed to remove member:', err);
      toast.error(err instanceof Error ? err.message : t('settings.failedToRemoveMember', 'Failed to remove member'));
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400';
      case 'admin':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400';
    }
  };

  const canManageMembers = () => {
    const currentMember = members.find((m) => m.userId === user?.id);
    return currentMember?.role === 'owner' || currentMember?.role === 'admin';
  };

  if (!currentOrganization) {
    return (
      <div className="p-6">
        <p className="text-neutral-500">{t('settings.noOrganization')}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-medium text-neutral-900 dark:text-white">
            {t('settings.members')}
          </h2>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            {t('settings.membersDescription')}
          </p>
        </div>
        {canManageMembers() && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
          >
            {t('settings.inviteMember')}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
            <thead className="bg-neutral-50 dark:bg-neutral-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  {t('settings.member')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  {t('settings.role')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  {t('settings.joined')}
                </th>
                {canManageMembers() && (
                  <th className="relative px-6 py-3">
                    <span className="sr-only">{t('common.actions')}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-900 divide-y divide-neutral-200 dark:divide-neutral-700">
              {members.map((member) => (
                <tr key={member.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {member.user.avatarUrl ? (
                          <img
                            className="h-10 w-10 rounded-full"
                            src={member.user.avatarUrl}
                            alt=""
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                            <span className="text-primary-600 dark:text-primary-400 font-medium text-sm">
                              {member.user.displayName?.charAt(0).toUpperCase() ??
                                member.user.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-neutral-900 dark:text-white">
                          {member.user.displayName ?? member.user.email}
                        </div>
                        <div className="text-sm text-neutral-500">
                          {member.user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {canManageMembers() && member.role !== 'owner' && member.userId !== user?.id ? (
                      <select
                        value={member.role}
                        onChange={(e) =>
                          handleUpdateRole(member.id, e.target.value as 'member' | 'admin')
                        }
                        className="text-sm rounded-md border border-neutral-300 bg-white text-neutral-900 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white"
                      >
                        <option value="member">{t('settings.roleMember')}</option>
                        <option value="admin">{t('settings.roleAdmin')}</option>
                      </select>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getRoleBadgeColor(
                          member.role
                        )}`}
                      >
                        {member.role}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                    {new Date(member.joinedAt).toLocaleDateString()}
                  </td>
                  {canManageMembers() && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {member.role !== 'owner' && member.userId !== user?.id && (
                        <button
                          onClick={() => handleRemoveMember(member.id, member.user.displayName ?? member.user.email)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          {t('common.remove')}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
              {t('settings.inviteMember')}
            </h3>

            {inviteError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{inviteError}</p>
              </div>
            )}

            {inviteSuccess && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-sm text-green-600 dark:text-green-400">
                  {t('settings.inviteSent')}
                </p>
              </div>
            )}

            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label
                  htmlFor="inviteEmail"
                  className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  {t('settings.emailAddress')}
                </label>
                <input
                  type="email"
                  id="inviteEmail"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@example.com"
                  className="mt-1 block w-full rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="inviteRole"
                  className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  {t('settings.role')}
                </label>
                <select
                  id="inviteRole"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'member' | 'admin')}
                  className="mt-1 block w-full rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
                >
                  <option value="member">{t('settings.roleMember')}</option>
                  <option value="admin">{t('settings.roleAdmin')}</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmail('');
                    setInviteError(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isInviting || !inviteEmail}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {isInviting ? t('common.sending') : t('settings.sendInvite')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {ConfirmDialogComponent}
    </div>
  );
}
